import { FONT_SANS } from '../../config/fonts';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Marker, Polyline, Popup, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";

import { useUnitContext } from "../../context/unit";
import { useAuthContext } from "../../context/auth";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

// ---------- Overlapping outposts ----------
// Two outposts "share a spot" when their icons would overlap on screen at the current zoom,
// so the check is done in pixels (map.project), not in lat/lng: 50 m apart overlaps when
// zoomed out but not when zoomed in. Overlapping outposts show as one marker with a count;
// clicking it fans them out around the real point ("spiderfy"), each with its own popup.
const OVERLAP_PX = 17;        // icon ring is 18px: closer than this = same spot
const FAN_MIN_RADIUS_PX = 24; // ring radius for a small group
const FAN_STEP_PX = 22;       // space between fanned markers along the ring

// One look for every outpost marker - alone, grouped or fanned out: a transparent ring with the
// outpost icon and a count badge ("1" for a single outpost) that shows only on hover.
// Icons are cached: a new icon object makes react-leaflet replace the marker element, and a
// marker whose element is replaced while hovered never gets "mouseout" (its tooltip would stay open).
const iconCache = new Map();

const createOutpostIcon = (count, isOnline, opacity = 1, isBlinking = false) => {
  const cacheKey = `${count}|${isOnline ? 1 : 0}|${opacity}|${isBlinking ? 1 : 0}`;
  if (iconCache.has(cacheKey)) return iconCache.get(cacheKey);

  const ring = isOnline ? `rgba(255, 0, 0, ${opacity})` : "rgba(170, 170, 170, 0.7)";
  const iconHtml = ReactDOMServer.renderToString(
    <GpsFixedIcon
      style={{
        color: isOnline ? `rgba(255, 0, 0, ${opacity})` : "rgba(120, 120, 120, 0.6)",
        fontSize: "11px",
        display: "block",
        filter: isOnline
          ? "drop-shadow(0 0 1px #00FFB4FF) drop-shadow(0 0 2px #00FFB3FF)"
          : "drop-shadow(0 0 2px #BBBBBB77)",
      }}
    />
  );

  const icon = L.divIcon({
    className: "outpost-marker",
    html: `
      <style>
        @keyframes radarBlink {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.2; transform: scale(1.6); }
          100% { opacity: 1; transform: scale(1); }
        }
        /* Count badge only while the marker is hovered: keeps the map clean.
           Pure CSS, so the cached icon is never replaced (see note above). */
        .outpost-marker .outpost-count {
          opacity: 0;
          transform: scale(0.6);
          transition: opacity .15s ease, transform .15s ease;
          pointer-events: none;
        }
        .outpost-marker:hover .outpost-count {
          opacity: 1;
          transform: scale(1);
        }
      </style>
      <div style="position:relative;width:18px;height:18px;display:grid;place-items:center;
                  border-radius:50%;background:transparent;border:1px solid ${ring};
                  box-shadow:${isOnline ? "0 0 6px #00FFB4AA, inset 0 0 4px #00FFB455" : "0 0 2px rgba(0,0,0,0.35)"};
                  animation:${isBlinking ? "radarBlink 0.5s ease-in-out 3" : "none"};">
        ${iconHtml}
        <span class="outpost-count" style="position:absolute;top:-7px;right:-8px;min-width:12px;height:12px;padding:0 3px;
                     box-sizing:border-box;border-radius:6px;
                     background:rgba(255, 209, 102, 0.4);border:1px solid rgba(255, 209, 102, 0.7);
                     color:#fff3d0;font:700 8px/10px ${FONT_SANS};text-align:center;
                     text-shadow:0 0 2px rgba(0,0,0,0.9);backdrop-filter:blur(1px);">${count}</span>
      </div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -9],
    tooltipAnchor: [9, 0],
  });

  iconCache.set(cacheKey, icon);
  return icon;
};

// Group outposts whose screen positions are within OVERLAP_PX of each other (chains count too)
const groupOverlapping = (map, outposts, zoom) => {
  const points = outposts.map((u) => map.project([u.latitude, u.longitude], zoom));
  const seen = new Array(outposts.length).fill(false);
  const groups = [];

  for (let i = 0; i < outposts.length; i++) {
    if (seen[i]) continue;
    seen[i] = true;
    const members = [i];

    for (let k = 0; k < members.length; k++) {
      const p = points[members[k]];
      for (let j = 0; j < outposts.length; j++) {
        if (!seen[j] && p.distanceTo(points[j]) < OVERLAP_PX) {
          seen[j] = true;
          members.push(j);
        }
      }
    }

    const units = members.map((m) => outposts[m]).sort((a, b) => (a.number ?? 0) - (b.number ?? 0));
    const center = members
      .reduce((acc, m) => acc.add(points[m]), L.point(0, 0))
      .divideBy(members.length);

    groups.push({
      key: units.map((u) => String(u._id)).join("|"),
      units,
      center,                                    // pixel point at this zoom
      centerLatLng: map.unproject(center, zoom),
    });
  }

  return groups;
};

// Fanned positions: a ring around the group's center, sized so markers don't touch
const fanPositions = (map, group, zoom) => {
  const n = group.units.length;
  const radius = Math.max(FAN_MIN_RADIUS_PX, (FAN_STEP_PX * n) / (2 * Math.PI));

  return group.units.map((_, i) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / n;      // first one straight up, then clockwise
    const p = group.center.add(L.point(Math.cos(angle) * radius, Math.sin(angle) * radius));
    return map.unproject(p, zoom);
  });
};

// Recursively flatten tree
const flattenUnits = (nodes = []) => {
  let result = [];

  nodes.forEach((node) => {
    result.push(node);

    if (node.children?.length > 0) {
      result = result.concat(flattenUnits(node.children));
    }
  });

  return result;
};

const OutpostMarker = ({ opacity = 1 }) => {
  const { auth } = useAuthContext();
  const axiosPrivate = useAxiosPrivate();

  const [blinkingUnits, setBlinkingUnits] = useState(new Set());

  const previousStatesRef = useRef({});

  const { selectableUnits = [], dispatch } = useUnitContext();

  const loadingRef = useRef(false);

  const fetchSelectableUnits = async (userId) => {
    if (loadingRef.current) return;

    loadingRef.current = true;

    try {
      const response = await axiosPrivate.get(
        "/api/units/selectable",
        {
          params: { userId },
        }
      );

      dispatch({
        type: "SET_SELECTABLE_UNITS",
        payload: response?.data,
      });

    } catch (error) {
      console.error("Failed to fetch selectable units:", error);

    } finally {
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    if (!auth?._id) return;

    // Initial fetch
    fetchSelectableUnits(auth._id);

    // Refresh every 3 seconds
    const interval = setInterval(() => {
      fetchSelectableUnits(auth._id);
    }, 3000);

    // Cleanup
    return () => clearInterval(interval);

  }, [auth?._id]);

  // Flatten all tree nodes
  const allUnits = useMemo(() => {
    if (!selectableUnits) return [];

    // selectableUnits may be object or array
    const roots = Array.isArray(selectableUnits)
      ? selectableUnits
      : [selectableUnits];

    return flattenUnits(roots);
  }, [selectableUnits]);

  // Filter only outposts (type 3)
  const outposts = useMemo(() => {
    return allUnits.filter(
      (unit) =>
        unit.type === 3 &&
        unit.latitude != null &&
        unit.longitude != null
    );
  }, [allUnits]);

  useEffect(() => {
    const newBlinking = new Set();

    outposts.forEach((unit) => {
      const previous =
        previousStatesRef.current[unit._id];

      // false -> true transition
      if (previous === false && unit.isOnline === true) {
        newBlinking.add(unit._id);
      }

      previousStatesRef.current[unit._id] =
        unit.isOnline;
    });

    if (newBlinking.size > 0) {
      setBlinkingUnits((prev) => {
        const updated = new Set(prev);

        newBlinking.forEach((id) => updated.add(id));

        return updated;
      });

      // Stop blinking after animation
      setTimeout(() => {
        setBlinkingUnits((prev) => {
          const updated = new Set(prev);

          newBlinking.forEach((id) => updated.delete(id));

          return updated;
        });
      }, 3000);
    }
  }, [outposts]);

  // ---------- Overlap groups (recomputed on every zoom) ----------
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());
  const [openGroupKey, setOpenGroupKey] = useState(null);   // the fanned-out group, if any

  useMapEvents({
    zoomend: () => setZoom(map.getZoom()),
    click: () => setOpenGroupKey(null),                        // click on the map: fold back
  });

  const groups = useMemo(() => groupOverlapping(map, outposts, zoom), [map, outposts, zoom]);

  // One line of the hover box: status dot, name (number), Online / Offline
  const renderStatusRow = (unit) => (
    <div key={unit._id} style={{ display: "flex", alignItems: "center", gap: 6, lineHeight: 1.6 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          flexShrink: 0,
          background: unit.isOnline ? "#43a047" : "#8a8fab",
        }}
      />
      <span>
        {unit.name}
        {unit.number != null && ` (${unit.number})`}
      </span>
      <span style={{ marginLeft: "auto", paddingLeft: 8, opacity: 0.7 }}>
        {unit.isOnline ? "Online" : "Offline"}
      </span>
    </div>
  );

  // Hover box shows on hover and hides on hover out (all markers)
  const hoverHandlers = { mouseout: (e) => e.target.closeTooltip() };

  const renderOutpost = (unit, position) => (
    <Marker
      key={unit._id}
      position={position}
      icon={createOutpostIcon(1, unit.isOnline, opacity, blinkingUnits.has(unit._id))}
      eventHandlers={hoverHandlers}
    >
      <Tooltip direction="right" offset={[6, 0]} opacity={1} className="outpost-tooltip">
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          1 outpost · {unit.isOnline ? "online" : "offline"}
        </div>
        {renderStatusRow(unit)}
        <div style={{ marginTop: 4, opacity: 0.7, fontSize: 11 }}>Click for details</div>
      </Tooltip>

      <Popup>
        <strong>{unit.name}</strong>

        {unit.number && (
          <>
            <br />
            Number: {unit.number}
          </>
        )}

        <br />
        Lat: {Number(unit.latitude).toFixed(6)}

        <br />
        Lng: {Number(unit.longitude).toFixed(6)}
      </Popup>
    </Marker>
  );

  return (
    <>
      {groups.map((group) => {
        // Alone on its spot: the usual marker
        if (group.units.length === 1) {
          const unit = group.units[0];
          return renderOutpost(unit, [unit.latitude, unit.longitude]);
        }

        // Fanned out: markers on a ring, thin lines back to the real point
        if (group.key === openGroupKey) {
          const positions = fanPositions(map, group, zoom);
          return (
            <React.Fragment key={group.key}>
              {group.units.map((unit, i) => (
                <Polyline
                  key={`leg-${unit._id}`}
                  positions={[[unit.latitude, unit.longitude], positions[i]]}
                  pathOptions={{ color: "#ffd166", weight: 1, opacity: 0.7, dashArray: "3 3", interactive: false }}
                />
              ))}
              {group.units.map((unit, i) => renderOutpost(unit, positions[i]))}
            </React.Fragment>
          );
        }

        // Folded: one marker with a count; hover lists them, click fans them out
        const onlineCount = group.units.filter((u) => u.isOnline).length;
        return (
          <Marker
            key={group.key}
            position={group.centerLatLng}
            icon={createOutpostIcon(group.units.length, onlineCount > 0, opacity)}
            eventHandlers={{
              ...hoverHandlers,
              click: () => setOpenGroupKey(group.key),
            }}
          >
            <Tooltip direction="right" offset={[6, 0]} opacity={1} className="outpost-tooltip">
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {group.units.length} outposts here · {onlineCount} online
              </div>
              {group.units.slice(0, 8).map(renderStatusRow)}
              {group.units.length > 8 && (
                <div style={{ opacity: 0.7 }}>+{group.units.length - 8} more</div>
              )}
              <div style={{ marginTop: 4, opacity: 0.7, fontSize: 11 }}>Click to show each outpost</div>
            </Tooltip>
          </Marker>
        );
      })}
    </>
  );
};

export default OutpostMarker;